/**
 * The state of the Belegjournal (`routes/documents.tsx`): filters, sorting and the page, read from
 * and written to the URL so a filtered view can be linked to and survives a reload:
 *
 * `?kind=ER&status=inbox,review&q=&from=&to=&partner=A&partner=B&min=&max=&category=&payment=&treatment=&sort=gross&dir=desc&page=2&size=50`
 *
 * Partner names may contain commas, so each one is its own `partner` parameter. Dates are days
 * (YYYY-MM-DD), both ends included. Pure: the PocketBase filter is built in `lib/api/documents.ts`.
 */
import type { DocumentKind, DocumentRecord, DocumentStatus } from "@/lib/api/documents";
import { TAX_TREATMENTS, type TaxTreatment } from "@/lib/at/vat";

export type PaymentStatus = DocumentRecord["paymentStatus"];

export type DocumentFilters = {
  kind: DocumentKind | "all";
  /** Empty: everything except archived documents. */
  statuses: DocumentStatus[];
  search: string;
  from: string;
  to: string;
  partners: string[];
  /** Gross amount, as typed ("" = no bound). */
  min: string;
  max: string;
  category: string;
  payment: PaymentStatus | "";
  treatment: TaxTreatment | "";
};

export const EMPTY_FILTERS: DocumentFilters = {
  kind: "all",
  statuses: [],
  search: "",
  from: "",
  to: "",
  partners: [],
  min: "",
  max: "",
  category: "",
  payment: "",
  treatment: "",
};

const KINDS: DocumentKind[] = ["ER", "AR", "KA"];
const STATUSES: DocumentStatus[] = ["inbox", "review", "booked", "archived"];
const PAYMENTS: PaymentStatus[] = ["open", "paid", "partial", "not_applicable"];
/** What "still open" means everywhere: freshly uploaded, or extracted but not confirmed. */
export const OPEN_STATUSES: DocumentStatus[] = ["inbox", "review"];

export type SortKey = "number" | "date" | "partner" | "gross" | "status";
export type SortDirection = "asc" | "desc";
export const SORT_KEYS: SortKey[] = ["number", "date", "partner", "gross", "status"];

export const PAGE_SIZES = [25, 50, 100] as const;
export const PAGE_SIZE = 50;

export type ListState = {
  filters: DocumentFilters;
  /** Undefined: newest first. */
  sort: SortKey | undefined;
  direction: SortDirection;
  page: number;
  size: number;
};

const DAY = /^\d{4}-\d{2}-\d{2}$/;
const day = (value: string | null) => (value && DAY.test(value) ? value : "");
const amount = (value: string | null) => {
  const normalized = value?.trim().replace(",", ".") ?? "";
  return normalized !== "" && Number.isFinite(Number(normalized)) ? String(Number(normalized)) : "";
};
const oneOf = <T extends string>(values: readonly T[], value: string | null): T | "" =>
  values.find((candidate) => candidate === value) ?? "";

export function parseListState(params: URLSearchParams): ListState {
  // `status=open` is what the dashboard links to.
  const statuses = (params.get("status") ?? "")
    .split(",")
    .flatMap((value) => (value === "open" ? OPEN_STATUSES : [value]))
    .filter((value): value is DocumentStatus => (STATUSES as string[]).includes(value));
  const size = Number(params.get("size"));
  return {
    filters: {
      kind: oneOf(KINDS, params.get("kind")) || "all",
      statuses: STATUSES.filter((status) => statuses.includes(status)),
      search: (params.get("q") ?? "").slice(0, 200),
      from: day(params.get("from")),
      to: day(params.get("to")),
      partners: params
        .getAll("partner")
        .map((name) => name.slice(0, 200))
        .filter(Boolean),
      min: amount(params.get("min")),
      max: amount(params.get("max")),
      category: /^[a-z0-9]{1,30}$/i.test(params.get("category") ?? "")
        ? params.get("category")!
        : "",
      payment: oneOf(PAYMENTS, params.get("payment")),
      treatment: oneOf(TAX_TREATMENTS, params.get("treatment")),
    },
    sort: oneOf(SORT_KEYS, params.get("sort")) || undefined,
    direction: params.get("dir") === "asc" ? "asc" : "desc",
    page: Math.max(1, Math.floor(Number(params.get("page"))) || 1),
    size: (PAGE_SIZES as readonly number[]).includes(size) ? size : PAGE_SIZE,
  };
}

/** The URL for a list state; defaults are left out so a plain list keeps a plain URL. */
export function listStateParams(state: ListState): URLSearchParams {
  const { filters } = state;
  const params = new URLSearchParams();
  const set = (key: string, value: string) => value && params.set(key, value);
  if (filters.kind !== "all") set("kind", filters.kind);
  set("status", filters.statuses.join(","));
  set("q", filters.search.trim());
  set("from", filters.from);
  set("to", filters.to);
  for (const partner of filters.partners) params.append("partner", partner);
  set("min", filters.min);
  set("max", filters.max);
  set("category", filters.category);
  set("payment", filters.payment);
  set("treatment", filters.treatment);
  if (state.sort) {
    set("sort", state.sort);
    set("dir", state.direction);
  }
  if (state.page > 1) set("page", String(state.page));
  if (state.size !== PAGE_SIZE) set("size", String(state.size));
  return params;
}

/** Same column flips the direction; a new column starts the way people read it. */
export function nextSort(
  state: Pick<ListState, "sort" | "direction">,
  key: SortKey,
): Pick<ListState, "sort" | "direction"> {
  if (state.sort === key)
    return { sort: key, direction: state.direction === "asc" ? "desc" : "asc" };
  // Newest and largest first; names and numbers A to Z.
  return { sort: key, direction: key === "date" || key === "gross" ? "desc" : "asc" };
}

/** The filters set in the sheet (type, search and status sit above the list). */
export function sheetFilterCount(filters: DocumentFilters): number {
  return [
    Boolean(filters.from || filters.to),
    filters.partners.length > 0,
    Boolean(filters.min || filters.max),
    Boolean(filters.category),
    Boolean(filters.payment),
    Boolean(filters.treatment),
  ].filter(Boolean).length;
}

export type DatePreset =
  "thisMonth" | "lastMonth" | "thisQuarter" | "lastQuarter" | "thisYear" | "lastYear";
export const DATE_PRESETS: DatePreset[] = [
  "thisMonth",
  "lastMonth",
  "thisQuarter",
  "lastQuarter",
  "thisYear",
  "lastYear",
];

const pad = (n: number) => String(n).padStart(2, "0");
export const toDay = (date: Date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
/** A YYYY-MM-DD day as a local date (new Date("2026-03-14") would be UTC midnight). */
export const parseDay = (value: string) => {
  const [year, month, dayOfMonth] = value.split("-").map(Number);
  return new Date(year!, month! - 1, dayOfMonth);
};

/** First and last day of a preset period, relative to `now`. */
export function presetRange(preset: DatePreset, now = new Date()): { from: string; to: string } {
  const year = now.getFullYear();
  const month = now.getMonth();
  const quarter = month - (month % 3);
  const span = (start: Date, end: Date) => ({ from: toDay(start), to: toDay(end) });
  switch (preset) {
    case "thisMonth":
      return span(new Date(year, month, 1), new Date(year, month + 1, 0));
    case "lastMonth":
      return span(new Date(year, month - 1, 1), new Date(year, month, 0));
    case "thisQuarter":
      return span(new Date(year, quarter, 1), new Date(year, quarter + 3, 0));
    case "lastQuarter":
      return span(new Date(year, quarter - 3, 1), new Date(year, quarter, 0));
    case "thisYear":
      return span(new Date(year, 0, 1), new Date(year, 11, 31));
    case "lastYear":
      return span(new Date(year - 1, 0, 1), new Date(year - 1, 11, 31));
  }
}

/** The preset a from/to pair matches, if any. */
export function matchingPreset(from: string, to: string, now = new Date()): DatePreset | "" {
  if (!from || !to) return "";
  return (
    DATE_PRESETS.find((preset) => {
      const range = presetRange(preset, now);
      return range.from === from && range.to === to;
    }) ?? ""
  );
}

/**
 * The page numbers to show: the first, the last and the current one with its neighbours;
 * "gap" marks skipped pages (1 … 4 5 6 … 20).
 */
export function pageItems(page: number, totalPages: number): (number | "gap")[] {
  const wanted = new Set([1, totalPages, page - 1, page, page + 1]);
  // Never hide a single page behind an ellipsis.
  if (page <= 4) [2, 3, 4, 5].forEach((n) => wanted.add(n));
  if (page >= totalPages - 3) [1, 2, 3, 4].forEach((n) => wanted.add(totalPages - n));
  const pages = [...wanted].filter((n) => n >= 1 && n <= totalPages).sort((a, b) => a - b);
  const items: (number | "gap")[] = [];
  pages.forEach((n, index) => {
    if (index > 0 && n - pages[index - 1]! > 1) items.push("gap");
    items.push(n);
  });
  return items;
}
